import { customElement, property, state } from "lit/decorators.js";
import { css, html, LitElement, nothing, PropertyValues } from "lit";
import { format, randstr } from "../../util/stringUtils";
import { addGlobalStylesToShadowRoot } from "../../util/globalStyleSheetsUtil";

@customElement("ehm-forminput")
export class Forminput extends LitElement {
  static formAssociated = true;
  static styles = css`
    :host {
      width: 100%;
    }
  `;

  private inputType: string;
  private label: string;
  private inputId: string;
  private formatdescription: string;
  private helptext: string;
  private selectoptions: string;
  private required: boolean;
  private name: string;
  private rows: string;
  private maxLength: string;
  private optiontoggle: string;
  private pattern: RegExp | undefined;
  private _handleFormData: any;
  private input: HTMLInputElement | undefined;
  private labelAsHtml: boolean;
  private hide: boolean;
  private disabled: boolean;
  private toggle: string[];
  private internals: ElementInternals;
  private accept: string;
  private conditionallyrequired: string;
  #mediaQuery = window.matchMedia("(max-width: 768px)");

  constructor() {
    super();
    this.internals = this.attachInternals();
    this.inputType = this.getAttribute("data-inputtype") ?? "text";
    this.labelAsHtml = this.getAttribute("data-labelashtml") === "true" ? true : false;
    this.label = this.getAttribute("data-label") ?? "label";
    this.formatdescription = this.getAttribute("data-formatdescription") ?? "";
    this.helptext = this.getAttribute("data-helptext") ?? "";
    this.name = this.getAttribute("data-name") ?? "";
    this.rows = this.getAttribute("data-rows") ?? "";
    this.maxLength = this.getAttribute("data-maxlength") ?? "";
    this.conditionallyrequired = this.getAttribute("data-conditionallyrequired") ?? "";
    this.optiontoggle = this.getAttribute("data-optiontoggle") ?? "";
    this.selectoptions = this.getAttribute("data-selectoptions") ?? "";
    this.hide = this.getAttribute("data-hide") === "true" ? true : false;
    this.disabled = this.getAttribute("data-disabled") === "true" ? true : false;
    this.toggle = this.getAttribute("data-toggle")?.split(",") ?? [];
    this.accept = this.getAttribute("data-accept") ?? "";
    this.required = this.getAttribute("data-required") === "true" ? true : false;
    this.inputId = this.getAttribute("data-id") ?? randstr("input-");

    this._handleFormData = this.handleFormData.bind(this);
  }

  firstUpdated(_changedProperties: PropertyValues): void {
    super.firstUpdated(_changedProperties);
    this.input = this.shadowRoot?.getElementById(this.inputId) as HTMLInputElement;
    if (this.inputType === "select") {
      if(this.required){
        this.input.setAttribute("required", "true")
      }
      const optionElem = document.createElement("option");
      optionElem.setAttribute("hidden", "true");
      optionElem.setAttribute("disabled", "true");
      optionElem.setAttribute("selected", "true");
      optionElem.setAttribute("value", "");
      optionElem.text = this.formatdescription;
      this.input?.appendChild(optionElem);
      if (this.selectoptions) {
        const options: OptionType[] = JSON.parse(this.selectoptions);
        options.forEach((option: OptionType) => {
          var elem = document.createElement("option");
          elem.setAttribute("value", option.Label)
          elem.text = option.Label;
          this.input?.appendChild(elem);
        });
      }
    }

    if (this.labelAsHtml && this.shadowRoot?.querySelector("label")) {
      this.shadowRoot!.querySelector("label")!.innerHTML = this.label;
    }
    if (this.disabled) {
      this.input.setAttribute("disabled", "true");
    }
    if (this.getAttribute("data-pattern")) {
      this.pattern = new RegExp(this.getAttribute("data-pattern")!);
    }
    addGlobalStylesToShadowRoot(this.shadowRoot!);
  }

  validate() {
    if (this.required && !this.input?.parentElement?.parentElement?.classList.contains("hidden")) {
        var errorMessageString = "";
      this.input?.classList.add("invalid");
      var errorMessage =
        this.input?.parentElement?.parentElement?.querySelector(".validationMessage") ?? this.input?.parentElement?.querySelector(".validationMessage");
      errorMessageString = this.input?.validationMessage ?? "";
      if(this.inputType==="select"){
        errorMessageString = "Gör ett val";
      }

      errorMessage?.classList.remove("hidden");
      if (errorMessage) {
        errorMessage!.textContent = errorMessageString;
      }
      if (this.inputType === "checkbox" && this.input?.checked) {
        this.input?.classList.remove("invalid");
        var errorMessage =
          this.input?.parentElement?.parentElement?.querySelector(".validationMessage");
        errorMessageString = this.input?.validationMessage;

        errorMessage?.classList.add("hidden");
      }
    }
    
    if (this.inputType !== "checkbox" && this.input?.value) {
      this.input?.classList.remove("invalid");

      if (this.input?.type === "checkbox") {
        this.input?.closest(".checkbox-input-container")?.classList.remove("invalid");
        this.input
          .closest(".form-section")
          ?.querySelector(".validationMessage")
          ?.classList.add("hidden");
      } else {
        this.input?.classList.remove("invalid");
        const message = this.input?.parentElement?.parentElement
          ?.querySelector(".validationMessage") ?? this.input?.parentElement
          ?.querySelector(".validationMessage")
        message?.classList.add("hidden");
      }
    }
    if (this.pattern && !this.input?.parentElement?.parentElement?.classList.contains("hidden")) {
      var errorMessage =
        this.input?.parentElement?.parentElement?.querySelector(".patternMessage");
      if (!this.pattern.test(this.input!.value)) {
        var errorMessageString = "";
        this.input?.classList.add("invalid");

        errorMessageString = this.formatdescription
          ? "Formatet stämmer inte (" + this.formatdescription + ")."
          : "Formatet stämmer inte.";

        errorMessage?.classList.remove("hidden");
        if (errorMessage) {
          errorMessage!.textContent = errorMessageString;
        }
      } else {
        errorMessage?.classList.add("hidden");
        this.input?.classList.remove("invalid");
      }

      if (!this.input?.value && !this.required) {
        errorMessage?.classList.add("hidden");
        this.input?.classList.remove("invalid");
      }
    }
  }

  handleFormData({ formData }: { formData: any }) {
    this.validate();
    if (this.inputType === "checkbox") {
      formData.append(this.input?.name, this.input?.checked ? "on" : "off");
    } else {
      formData.append(this.input?.name, this.input?.value);
    }
  }

  connectedCallback() {
    super.connectedCallback();
    const form = this.findContainingForm();
    if (form) {
      form.addEventListener("formdata", this._handleFormData);
    }
  }

  findContainingForm() {
    const root = this.getRootNode();
    const forms = Array.from((root as HTMLElement).querySelectorAll("form"));
    return forms.find((form) => form.contains(this)) || null;
  }
  onClearInput(e: Event): void {
    e.preventDefault();
    if (this.shadowRoot?.getElementById(this.inputId)) {
      (this.shadowRoot.getElementById(this.inputId) as HTMLInputElement)!.value = "";
    }
    (e.target as HTMLButtonElement).classList.add("hidden");
  }

  onChange(e: InputEvent) {
    this.validate();
    this.internals.setFormValue(this.input?.value ?? "");
    if ((e.target as any).value) {
      (e.target as any).parentElement
        ?.querySelector(".input-clear")
        ?.classList.remove("hidden");
    } else {
      (e.target as HTMLInputElement).parentElement
        ?.querySelector(".input-clear")
        ?.classList.add("hidden");
    }
  }

  onKeyUp(e: KeyboardEvent) {
    this.validate();
    this.internals.setFormValue(this.input?.value ?? "");
    if(this.maxLength && this.inputType === "textarea"){
      const maxL = this.maxLength;
      const count = this.shadowRoot?.querySelector(".message_count");
      if(count){
        count.textContent = (e.target as any).value.length+'/'+maxL 
      }
      
    }
    if ((e.target as any).value) {
      (e.target as any).parentElement
        ?.querySelector(".input-clear")
        ?.classList.remove("hidden");
    } else {
      (e.target as HTMLInputElement).parentElement
        ?.querySelector(".input-clear")
        ?.classList.add("hidden");
    }
  }

  onFocus(e: InputEvent) {
    if (this.inputType === "date") {
      this.input?.showPicker();
    }
  }

  onSelectChange(e: InputEvent) {
    this.validate();
    if ((e.target as any).value) {
      this.internals.setFormValue(this.input?.value ?? "");
      if (this.optiontoggle) {
        const form = this.findContainingForm();
        const forminput = form?.querySelector(
          'ehm-forminput[data-id="' + this.optiontoggle + '"]',
        );
        const elem = forminput?.shadowRoot?.querySelector("#" + this.optiontoggle);
        if (elem) {
          elem.innerHTML = "";
          const optionElem = document.createElement("option");
          optionElem.setAttribute("hidden", "true");
      optionElem.setAttribute("disabled", "true");
      optionElem.setAttribute("selected", "true");
      optionElem.setAttribute("value", "");
      if(forminput){
        optionElem.text = forminput.getAttribute("data-formatdescription") ?? "";
      }
          elem?.appendChild(optionElem);
          const options: OptionType[] = JSON.parse(this.selectoptions);
          options
            .find((x) => x.Label === (e.target as any).value)
            ?.SubLabels?.forEach((sublabel: string) => {
              var optionElem = document.createElement("option");
              optionElem.setAttribute("value", sublabel)
              optionElem.text = sublabel;
              elem?.appendChild(optionElem);
              elem?.removeAttribute("disabled");
            });
        }
      }
    }
  }

  hasVisibleInput(container : Element) {
    let hasVisibleInput = false;
    const ehmInputs = container.querySelectorAll("ehm-forminput");
    ehmInputs.forEach((input) => {
      if(input.shadowRoot && input.shadowRoot.querySelectorAll(".forminput-container:not(.hidden)")?.length > 0){
        hasVisibleInput = true;
      }
    })
    return hasVisibleInput;
  }

  onChecked(e: InputEvent) {
    this.validate();
    const form = this.findContainingForm();
    if(this.conditionallyrequired){
      const closest = this.closest(".required-form-section");
      const conditionInput = form?.querySelector('ehm-forminput[data-id="' + this.conditionallyrequired + '"]')?.shadowRoot?.querySelector("input");
      if(conditionInput?.checked || this.input?.checked){
        closest?.classList.remove("invalid");
        closest?.classList.remove("changed-invalid");
        closest?.querySelector(".validationMessage")?.classList.add("hidden");
      }
      else{
        
        closest?.classList.add("invalid");
        closest?.classList.add("changed-invalid");
        closest?.querySelector(".validationMessage")?.classList.remove("hidden");
      }
    }
    if (this.toggle.length > 0) {
      this.toggle.forEach((id) => {
        
        const forminput = form?.querySelector('ehm-forminput[data-id="' + id + '"]');
        const elem = 
          forminput?.shadowRoot?.querySelector("#" + id)?.parentElement?.parentElement;

          const closestFormSection = forminput?.closest(".form-section");
          const closestInputContainer = forminput?.closest(".input-container");
          
          if(this.input?.checked){
            elem?.classList.remove("hidden")
            closestInputContainer?.classList.remove("hidden")
          }
          else{
            elem?.classList.add("hidden");
            closestInputContainer?.classList.add("hidden");
          }
          if(closestFormSection && this.hasVisibleInput(closestFormSection)){
            closestFormSection?.classList.remove("hidden")
          }
          else{
            closestFormSection?.classList.add("hidden")
          }
      });
    }
  }

  render() {
    return html`
      ${this.inputType === "text" ||
      this.inputType === "date" ||
      this.inputType === "tel" ||
      this.inputType === "email"
        ? html`
            <div class="forminput-container ${this.hide ? "hidden" : ""}">
              <label for="${this.inputId}" class="input-label">${this.label}</label>
              ${this.helptext && html`<div class="input-helptext">${this.helptext}</div>`}
              ${this.formatdescription &&
              html`<div class="input-formatdescription">(${this.formatdescription})</div>`}
              <div class="validationMessage hidden"></div>
              <div class="patternMessage hidden"></div>
              <div class="input-wrapper">
                ${this.required
                  ? html`<input
                      class="text-form-input"
                      @keyup=${(e: KeyboardEvent) => {
                        if (e.key !== "Tab") this.onKeyUp(e);
                      }}
                      @blur=${(e: InputEvent) => this.onChange(e)}
                      type="${this.inputType}"
                      required
                      @focus=${(e: InputEvent) => this.onFocus(e)}
                      id="${this.inputId}"
                      name="${this.name}"
                    />`
                  : html`<input
                      class="text-form-input"
                      rows="${this.rows}"
                      maxlength="${this.maxLength}"
                      type="${this.inputType}"
                      @keyup=${(e: KeyboardEvent) => {
                        if (e.key !== "Tab") this.onKeyUp(e);
                      }}
                      @blur=${(e: InputEvent) => this.onChange(e)}
                      @focus=${(e: InputEvent) => this.onFocus(e)}
                      id="${this.inputId}"
                      name="${this.name}"
                    />`}
                <button
                  @click=${(e: InputEvent) => this.onClearInput(e)}
                  class="input-clear hidden"
                ></button>
              </div>
            </div>
          `
        : this.inputType === "file"
          ? html` ${this.required
                    ?
              html`<label for="${this.inputId}" class="file-label">${this.label}</label>

              ${this.helptext && html`<div class="input-helptext">${this.helptext}</div>`}
              ${this.formatdescription &&
              html`<div class="formatdescription">(${this.formatdescription})</div>`}
              <div class="input-wrapper">
                <input
                  type="${this.inputType}"
                  id="${this.inputId}"
                  name="${this.name}"
                  accept="${this.accept}"
                  required
                  @change=${(e: InputEvent) => this.onChange(e)}
                />
                <div class="validationMessage hidden">Otillåten filtyp</div>
              </div> ` :
              html` <label for="${this.inputId}" class="file-label">${this.label}</label>

              ${this.helptext && html`<div class="input-helptext">${this.helptext}</div>`}
              ${this.formatdescription &&
              html`<div class="formatdescription">(${this.formatdescription})</div>`}
              <div class="input-wrapper">
                <div class="validationMessage hidden">Otillåten filtyp</div>
                <input
                  type="${this.inputType}"
                  id="${this.inputId}"
                  name="${this.name}"
                  accept="${this.accept}"
                  @change=${(e: InputEvent) => this.onChange(e)}
                />
              </div> `}
            `
          : this.inputType === "select"
            ? html`
            <div>
            <label for="${this.inputId}" class="input-label">${this.label}</label>
            <div class="validationMessage hidden"></div>
            <div class="input-wrapper">
                <select
                  id="${this.inputId}"
                  @change=${(e: InputEvent) => this.onSelectChange(e)}
                  id="${this.inputId}"
                  name="${this.name}"
                  aria-label="${this.label}"
                >
                  
                </select>
                <div class="caret"></div>
                </div>
                </>
              `
            : this.inputType === "radio"
              ? html``
              : this.inputType === "checkbox"
                ? html` ${this.required
                    ? html`
                    <div class="forminput-container ${this.hide ? "hidden" : ""}">
                    <div class="validationMessage hidden"></div>
                        <div class="input-wrapper checkbox-content">
                          
                          <input
                            type="checkbox"
                            class="form-checkbox"
                            required
                            id="${this.inputId}"
                            name="${this.name}"
                            @change=${(e: InputEvent) => this.onChecked(e)}
                          />
                          <label for="${this.inputId}">${this.label}</label>
                        </div>
                        </div>
                      `
                    : html`
                        <div class="input-wrapper checkbox-content">
                          <input
                            type="checkbox"
                            class="form-checkbox"
                            id="${this.inputId}"
                            name="${this.name}"
                            @change=${(e: InputEvent) => this.onChecked(e)}
                          />
                          <label for="${this.inputId}">${this.label}</label>
                        </div>
                      `}`
                : this.inputType === "textarea"
                  ? html` ${this.required
                      ? html`
                      <div class="forminput-container ${this.hide ? "hidden" : ""}">
                          <label for="${this.inputId}" class="input-label"
                            >${this.label}</label
                          >
                            ${this.helptext && html`<div class="input-helptext">${this.helptext}</div>`}
                            ${this.formatdescription &&
                            html`<div class="input-formatdescription">(${this.formatdescription})</div>`}
                            <div class="validationMessage hidden"></div>
                            <div class="patternMessage hidden"></div>
                          <div class="input-wrapper">
                            
                            <textarea
                              class="text-form-input"
                              rows="${this.rows}"
                              maxlength="${this.maxLength}"
                              @keyup=${(e: KeyboardEvent) => {
                                if (e.key !== "Tab") this.onKeyUp(e);
                              }}
                              @blur=${(e: InputEvent) => this.onChange(e)}
                              type="${this.inputType}"
                              required
                              @focus=${(e: InputEvent) => this.onFocus(e)}
                              id="${this.inputId}"
                              name="${this.name}"
                            ></textarea>
                            ${this.maxLength ? html`<span class="message_count">0/${this.maxLength}</span>` : ''}
                          </div>
                          </div>
                        `
                      : html`
                      <div class="forminput-container ${this.hide ? "hidden" : ""}">
                      
                          <label for="${this.inputId}" class="input-label"
                            >${this.label}</label
                          >
                            ${this.helptext && html`<div class="input-helptext">${this.helptext}</div>`}
                            ${this.formatdescription &&
                            html`<div class="input-formatdescription">(${this.formatdescription})</div>`}
                            <div class="patternMessage hidden"></div>
                            <div class="input-wrapper">
                          <textarea
                            class="text-form-input"
                            rows="${this.rows}"
                            maxlength="${this.maxLength}"
                            @keyup=${(e: KeyboardEvent) => {
                              if (e.key !== "Tab") this.onKeyUp(e);
                            }}
                            @blur=${(e: InputEvent) => this.onChange(e)}
                            type="${this.inputType}"
                            @focus=${(e: InputEvent) => this.onFocus(e)}
                            id="${this.inputId}"
                            name="${this.name}"
                          ></textarea>
                          ${this.maxLength ? html`<span class="message_count">0/${this.maxLength}</span>` : ''}
                          </div>
                          </div>
                        `}`
                  : ""}
    `;
  }
}
